import { FC } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "@/shared/hooks";
import { isExternalUrl } from "@/shared/utils";
import { DEFAULT_TIMECODE_ICON, ICON_MAP } from "./timecodeIcons";
import type { PlayerTimecode } from "./playerTimecode";

// Высота одной аннотации для стекирования (референсный ANNOTATION_HEIGHT)
const ANNOTATION_HEIGHT = 120;
const GAP = 16;

// Порт референсного VideoAnnotation.js. Адаптация: у нас нет
// PlayerOccupiedZonesContext, поэтому отступы статические: сверху spacing(9),
// под кнопкой сворачивания окна (слева) и кнопкой виджета (справа), снизу
// spacing(10) — над футером контролов;
// боковые — как у кнопки «Далее» (1 / 2@375px / 3@md). Стекирование
// нескольких аннотаций в одном углу — через index.
const Root = styled("div", {
  shouldForwardProp: (prop) => prop !== "position" && prop !== "index",
})<{ position: string; index: number }>(({ theme, position, index }) => {
  const isTop = position === "TOP_LEFT" || position === "TOP_RIGHT";
  const isLeft = position === "TOP_LEFT" || position === "BOTTOM_LEFT";
  const stackOffset = index * (ANNOTATION_HEIGHT + GAP);

  return {
    position: "absolute",
    ...(isTop
      ? { top: `calc(${theme.spacing(9)} + ${stackOffset}px)` }
      : { bottom: `calc(${theme.spacing(10)} + ${stackOffset}px)` }),
    ...(isLeft ? { left: theme.spacing(1) } : { right: theme.spacing(1) }),
    ["@media (min-width: 375px)"]: {
      ...(isLeft ? { left: theme.spacing(2) } : { right: theme.spacing(2) }),
    },
    [theme.breakpoints.up("md")]: {
      ...(isLeft ? { left: theme.spacing(3) } : { right: theme.spacing(3) }),
    },
    maxWidth: theme.spacing(43.75),
    width: `calc(100% - ${theme.spacing(4)})`,
    zIndex: 200,
    pointerEvents: "auto",
    animation: `videoAnnotationFadeIn ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeIn}`,
    "@keyframes videoAnnotationFadeIn": {
      from: { opacity: 0, transform: "translateY(-10px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    // Мобилка: только вертикальное позиционирование, по центру (референс)
    [theme.breakpoints.down("sm")]: {
      left: "50%",
      right: "auto",
      transform: "translateX(-50%)",
      maxWidth: theme.spacing(30),
      width: `calc(100% - ${theme.spacing(2)})`,
      animation: `videoAnnotationFadeInMobile ${theme.transitions.duration.enteringScreen}ms ${theme.transitions.easing.easeIn}`,
      "@keyframes videoAnnotationFadeInMobile": {
        from: { opacity: 0, transform: "translateX(-50%) translateY(-10px)" },
        to: { opacity: 1, transform: "translateX(-50%) translateY(0)" },
      },
    },
  };
});

interface VideoAnnotationProps {
  annotation: PlayerTimecode;
  onClose: () => void;
  index?: number;
}

export const VideoAnnotation: FC<VideoAnnotationProps> = ({
  annotation,
  onClose,
  index = 0,
}) => {
  const { t } = useTranslation();
  const IconComponent = ICON_MAP[annotation.icon] ?? DEFAULT_TIMECODE_ICON;
  const linkedContent = annotation.linkedContent;
  const linkedTitle =
    linkedContent && linkedContent.title.length > 30
      ? `${linkedContent.title.substring(0, 30)}...`
      : linkedContent?.title;

  return (
    <Root position={annotation.position ?? "TOP_LEFT"} index={index}>
      <Card
        sx={(theme) => ({
          backgroundColor: "rgba(30, 30, 30, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: `${Number(theme.shape.borderRadius) * 1.5}px`,
          boxShadow: theme.shadows[4],
          position: "relative",
          [theme.breakpoints.down("sm")]: {
            borderRadius: `${theme.shape.borderRadius}px`,
          },
        })}
      >
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t("common.close")}
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(1),
            right: theme.spacing(1),
            color: "rgba(255, 255, 255, 0.7)",
            "&:hover": {
              color: "white",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
            [theme.breakpoints.down("sm")]: {
              top: theme.spacing(0.25),
              right: theme.spacing(0.25),
              padding: theme.spacing(0.5),
              "& svg": { fontSize: theme.spacing(2) },
            },
          })}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <CardContent
          sx={(theme) => ({
            p: 2,
            "&:last-child": { paddingBottom: theme.spacing(2) },
            [theme.breakpoints.down("sm")]: {
              p: 0.75,
              "&:last-child": { paddingBottom: theme.spacing(0.75) },
            },
          })}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              [theme.breakpoints.down("sm")]: { gap: 0.375, mb: 0.375 },
            })}
          >
            <Box
              sx={(theme) => ({
                width: theme.spacing(3),
                height: theme.spacing(3),
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "& svg": { fontSize: theme.spacing(2), color: "white" },
                [theme.breakpoints.down("sm")]: {
                  width: theme.spacing(1.75),
                  height: theme.spacing(1.75),
                  "& svg": { fontSize: theme.spacing(1.25) },
                },
              })}
            >
              <IconComponent />
            </Box>
            <Typography
              variant="subtitle1"
              sx={(theme) => ({
                color: "white",
                fontWeight: 600,
                flex: 1,
                fontSize: "0.95rem",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.65rem",
                  lineHeight: 1.2,
                },
              })}
            >
              {annotation.title}
            </Typography>
          </Box>
          {annotation.description && (
            <Typography
              variant="body2"
              sx={(theme) => ({
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "0.875rem",
                lineHeight: 1.5,
                "& a": {
                  color: "#64B5F6",
                  textDecoration: "underline",
                  "&:hover": { color: "#90CAF9" },
                },
                "& p": {
                  margin: 0,
                  "&:not(:last-child)": { marginBottom: "8px" },
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.6rem",
                  lineHeight: 1.25,
                },
              })}
              // HTML отрендерен из markdown на бэке (site ContentTimecodes
              // резолвер) — как в референсе.
              dangerouslySetInnerHTML={{ __html: annotation.description }}
            />
          )}
          {linkedContent && (
            <Box
              component="a"
              href={linkedContent.path}
              {...(isExternalUrl(linkedContent.path)
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              sx={(theme) => ({
                mt: 1,
                display: "flex",
                gap: 1,
                alignItems: "center",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { opacity: 0.85 },
                [theme.breakpoints.down("sm")]: { gap: 0.5, mt: 0.5 },
              })}
            >
              {linkedContent.preview?.url && (
                <Box
                  component="img"
                  src={String(linkedContent.preview.url)}
                  alt={linkedContent.title}
                  sx={(theme) => ({
                    height: theme.spacing(6),
                    width: "auto",
                    borderRadius: "5px",
                    objectFit: "cover",
                    flexShrink: 0,
                    [theme.breakpoints.down("sm")]: {
                      height: theme.spacing(4),
                    },
                  })}
                />
              )}
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "0.55rem",
                    lineHeight: 1.2,
                  },
                })}
              >
                {linkedTitle}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Root>
  );
};
